import React, { createContext, useState, ReactNode, useContext, useMemo, useRef } from 'react';

interface FetchContextType {
    activeFetches: Set<string>;
    registerFetch: (url: string) => void;
    unregisterFetch: (url: string) => void;
    getCache: (url: string, params?: any) => any;
    setCache: (url: string, data: any, params?: any) => void;
    invalidate: (url: string, params?: any) => void;
    clearCache: () => void;
}

const FetchContext = createContext<FetchContextType | undefined>(undefined);

interface FetchProviderProps {
    children: ReactNode;
}

interface CacheEntry {
    data: any;
    timestamp: number;
}

// Set the Time-to-live in milliseconds
const TTL_MS = 1000 * 60 *5;

// Generate unique cache key based on url and params
const generateCacheKey = (url: string, params?: any): string => {
    if (!params || Object.keys(params).length === 0) return url;
    return `${url}?${JSON.stringify(params, Object.keys(params).sort())}`;
};

export const FetchContextProvider: React.FC<FetchProviderProps> = ({ children }) => {
    const [activeFetches, setActiveFetches] = useState<Set<string>>(new Set());
    const cacheRef = useRef<Map<String, CacheEntry>>(new Map());

    // Register a fetch url to the active fetches
    const registerFetch = (url: string) => {
        setActiveFetches(prev => new Set(prev.add(url)));
    };

    // Unregister a complete fetch from the active fetches
    const unregisterFetch = (url: string) => {
        setActiveFetches(prev => {
            const newSet = new Set(prev);
            newSet.delete(url);
            return newSet;
        });
    };

    // Get the data from the cache
    const getCache = (url: string, params?: any): any => {
        const key = generateCacheKey(url, params);
        const entry = cacheRef.current.get(key);

        if (!entry) return null;

        // Check wheter the cached data is valid
        const now = Date.now();
        if (now - entry.timestamp > TTL_MS) {

            // Delete the cached data if the time is expired
            cacheRef.current.delete(key);
            return null;
        }

        // Return the data
        return entry.data;
    };

    // Save the data in cache with timestamp
    const setCache = (url: string, data: any, params?: any): void => {
        const key = generateCacheKey(url, params);
        cacheRef.current.set(key, {
            data,
            timestamp: Date.now()
        });
    };

    // Invalidate a specific cached data entry to refetch it later
    const invalidate = (url: string, params?: any) => {
        const key = generateCacheKey(url, params);
        cacheRef.current.delete(key);
    };

    // Clear all cache entries
    const clearCache = () => {
        cacheRef.current.clear();
    };

    // Memoize the context value to prevent rerendering all consumers on every provider render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const contextValue = useMemo(() => ({
        activeFetches,
        registerFetch,
        unregisterFetch,
        getCache,
        setCache,
        invalidate,
        clearCache
    }), [activeFetches]);

    return (
        <FetchContext.Provider value={contextValue}>
            {children}
        </FetchContext.Provider>
    );
};

// Create a custom hook for the fetch context
export const useFetchContext = () => {
    const context = useContext(FetchContext);
    if (!context) {
        throw new Error('useFetchContext must be used within a FetchProvider');
    }
    return context;
};